import { detectLocale, type AppLocale } from './locale';

export type BillingLocale = AppLocale;

export const BILLING_LEGAL_URLS = {
  privacyPolicy: 'https://apps.tomippe.jp/pouches/policy/',
  appleStandardEula: 'https://www.apple.com/legal/internet-services/itunes/dev/stdeula/',
} as const;

export function detectBillingLocale(): BillingLocale {
  return detectLocale();
}

export type BillingCopy = {
  paywallTitle: string;
  paywallBody: (sheetCount: number, freeMax: number) => string;
  purchase: (price: string) => string;
  purchaseFallback: string;
  restore: string;
  manage: string;
  close: string;
  platformNote: string;
  platformNoteIosNative: string;
  platformNoteMacNative: string;
  storeProductLoading: string;
  storePurchaseProcessingAppStore: string;
  subscriptionLegalTitle: string;
  subscriptionProductName: string;
  subscriptionDuration: string;
  subscriptionPriceLine: (price: string) => string;
  subscriptionPriceFallback: string;
  subscriptionAutoRenew: string;
  privacyPolicyLinkLabel: string;
  termsOfUseLinkLabel: string;
  freeHint: (remaining: number) => string;
  proActive: string;
  paywallProManageBody: string;
  paywallLegacyProBody: string;
  sidebarPro: string;
  sidebarFreeRemaining: (remaining: number) => string;
  sidebarFreeLimit: string;
  sidebarStatusClickSubscribe: string;
  sidebarStatusClickManage: string;
  sidebarStatusClickRedeem: string;
  sidebarStatusClickIssueCode: string;
  purchaseFailedTitle: string;
  storePurchaseProcessing: string;
  storePurchaseOpenInStore: string;
  restoreNone: string;
  restoreFailedTitle: string;
  webRedeemTitle: string;
  webRedeemBody: string;
  webRedeemInputPlaceholder: string;
  webRedeemInputHint: string;
  webRedeemActivate: string;
  webRedeemStoreNote: string;
  webRedeemIssueTitle: string;
  webRedeemIssueProcessing: string;
  webRedeemIssueResultLead: string;
  webRedeemIssueNote: string;
  webRedeemIssueRetry: string;
  webRedeemIssueRegenerate: string;
  webRedeemIssueCopy: string;
  webRedeemIssueCopied: string;
  webRedeemIssueExpires: (expiresAt: string) => string;
  webRedeemIssueOpenLink: string;
};

const COPY: Record<BillingLocale, BillingCopy> = {
  ja: {
    paywallTitle: 'POUCHES Pro が必要です',
    paywallBody: (sheetCount, freeMax) =>
      `無料ではシートを${freeMax}枚まで作成できます（現在 ${sheetCount} 枚）。4枚目以降は POUCHES Pro（月額）のご加入が必要です。`,
    purchase: (price) => `${price}/月で POUCHES Pro に加入`,
    purchaseFallback: '月額 ¥400（税込）で POUCHES Pro に加入',
    restore: '購入を復元',
    manage: 'サブスクリプションを管理',
    close: '閉じる',
    platformNote:
      'POUCHES Pro のご購入は App Store / Microsoft Store で行い、Web 版は引き換えコードで有効化します。',
    platformNoteIosNative:
      'このアプリから App Store アカウントで POUCHES Pro に加入できます。Web 版は加入後に引き換えコードを発行できます。',
    platformNoteMacNative:
      'このアプリから App Store で POUCHES Pro に加入できます。Web 版は加入後に引き換えコードを発行できます。',
    storeProductLoading: 'App Store から価格を取得しています…',
    storePurchaseProcessingAppStore: 'App Store で購入処理を完了しています…',
    subscriptionLegalTitle: 'サブスクリプション:',
    subscriptionProductName: 'POUCHES Pro（月額・自動更新）',
    subscriptionDuration: '期間: 1か月（自動更新）',
    subscriptionPriceLine: (price) => `価格: ${price}/月`,
    subscriptionPriceFallback: '価格: ¥400/月（税込）',
    subscriptionAutoRenew:
      '購入確認時に Apple ID に請求されます。現在の期間終了の24時間前までに解約しない限り自動更新されます。App Store のアカウント設定から管理・解約できます。',
    privacyPolicyLinkLabel: 'プライバシーポリシー',
    termsOfUseLinkLabel: '利用規約（EULA）',
    freeHint: (remaining) =>
      remaining > 0
        ? `無料で残り ${remaining} シート作成できます`
        : '無料枠の上限に達しています',
    proActive: 'POUCHES Pro 有効',
    paywallProManageBody:
      'POUCHES Pro に加入中です。サブスクリプションの確認・解除は「サブスクリプションを管理」から行えます。',
    paywallLegacyProBody:
      '既存ユーザー向けの Pro 特典が有効です。Web 版を使う場合は、ここから Web 版用引き換えコードを発行してください。',
    sidebarPro: 'Pro有効',
    sidebarFreeRemaining: (remaining) => `残り${remaining}シート`,
    sidebarFreeLimit: '残り0シート',
    sidebarStatusClickSubscribe: 'サブスクに加入',
    sidebarStatusClickManage: 'サブスクを管理・解除',
    sidebarStatusClickRedeem: '引き換えコードを入力',
    sidebarStatusClickIssueCode: 'Web版用コードを発行',
    purchaseFailedTitle: '購入を開始できませんでした',
    storePurchaseProcessing: 'Microsoft Store を開いています…',
    storePurchaseOpenInStore: 'Microsoft Store で POUCHES を開く',
    restoreNone: '復元できる購入が見つかりませんでした',
    restoreFailedTitle: '復元に失敗しました',
    webRedeemTitle: 'Web版用引き換えコード',
    webRedeemBody:
      'App Store または Microsoft Store で POUCHES Pro を購入した方は、発行したコードを入力して Web 版を有効化できます。',
    webRedeemInputPlaceholder: '12桁の数字を入力',
    webRedeemInputHint: 'ハイフンは不要。数字12桁を入力してください。',
    webRedeemActivate: '有効化',
    webRedeemStoreNote: 'App Store / Microsoft Store で POUCHES Pro を購入してください。',
    webRedeemIssueTitle: 'Web版用引き換えコード',
    webRedeemIssueProcessing: 'コードを発行しています…',
    webRedeemIssueResultLead:
      'ブラウザで apps.tomippe.jp を開き、次のコードを入力してください。',
    webRedeemIssueNote:
      '1コードは1ブラウザのみ（48時間）。別ブラウザでは新しいコードを発行してください。',
    webRedeemIssueRetry: '再試行',
    webRedeemIssueRegenerate: '新しいコードを発行',
    webRedeemIssueCopy: 'コピー',
    webRedeemIssueCopied: 'コピーしました',
    webRedeemIssueExpires: (expiresAt) => `有効期限: ${expiresAt}`,
    webRedeemIssueOpenLink: 'Web版用コードを発行',
  },
  en: {
    paywallTitle: 'POUCHES Pro required',
    paywallBody: (sheetCount, freeMax) =>
      `You can create up to ${freeMax} sheets for free (currently ${sheetCount}). POUCHES Pro (monthly) is required for a 4th sheet and beyond.`,
    purchase: (price) => `Subscribe to POUCHES Pro for ${price}/month`,
    purchaseFallback: 'Subscribe to POUCHES Pro for ¥400/month (tax incl.)',
    restore: 'Restore purchase',
    manage: 'Manage subscription',
    close: 'Close',
    platformNote:
      'Purchase POUCHES Pro on the App Store or Microsoft Store, then activate the Web app with a redeem code.',
    platformNoteIosNative:
      'Subscribe in this app with your App Store account. After subscribing, you can issue a redeem code for the Web app.',
    platformNoteMacNative:
      'Subscribe in this app with your App Store account. After subscribing, you can issue a redeem code for the Web app.',
    storeProductLoading: 'Loading subscription price from the App Store…',
    storePurchaseProcessingAppStore: 'Completing App Store purchase…',
    subscriptionLegalTitle: 'Subscription:',
    subscriptionProductName: 'POUCHES Pro Monthly (auto-renewable)',
    subscriptionDuration: 'Length: 1 month (auto-renewing)',
    subscriptionPriceLine: (price) => `Price: ${price}/month`,
    subscriptionPriceFallback: 'Price: ¥400/month (tax incl.)',
    subscriptionAutoRenew:
      'Payment is charged to your Apple ID at confirmation of purchase. The subscription automatically renews unless canceled at least 24 hours before the end of the current period. Manage or cancel in App Store account settings.',
    privacyPolicyLinkLabel: 'Privacy Policy',
    termsOfUseLinkLabel: 'Terms of Use (EULA)',
    freeHint: (remaining) =>
      remaining > 0
        ? `${remaining} more sheet${remaining === 1 ? '' : 's'} available on the free plan`
        : 'You have reached the free sheet limit',
    proActive: 'POUCHES Pro active',
    paywallProManageBody:
      'You are subscribed to POUCHES Pro. Use Manage subscription to review or cancel.',
    paywallLegacyProBody:
      'Your grandfathered Pro access is active. Issue a Web redeem code here to use POUCHES Pro in the browser.',
    sidebarPro: 'Pro active',
    sidebarFreeRemaining: (remaining) =>
      `${remaining} sheet${remaining === 1 ? '' : 's'} left`,
    sidebarFreeLimit: 'Free limit',
    sidebarStatusClickSubscribe: 'Subscribe',
    sidebarStatusClickManage: 'Manage or cancel subscription',
    sidebarStatusClickRedeem: 'Enter redeem code',
    sidebarStatusClickIssueCode: 'Issue Web redeem code',
    purchaseFailedTitle: 'Could not start purchase',
    storePurchaseProcessing: 'Opening Microsoft Store…',
    storePurchaseOpenInStore: 'Open POUCHES in Microsoft Store',
    restoreNone: 'No purchase found to restore',
    restoreFailedTitle: 'Restore failed',
    webRedeemTitle: 'Web redeem code',
    webRedeemBody:
      'If you subscribed to POUCHES Pro on the App Store or Microsoft Store, enter the issued code to activate the Web app.',
    webRedeemInputPlaceholder: 'Enter 12-digit code',
    webRedeemInputHint: 'Hyphens are optional. Enter 12 digits.',
    webRedeemActivate: 'Activate',
    webRedeemStoreNote: 'Subscribe to POUCHES Pro on the App Store or Microsoft Store.',
    webRedeemIssueTitle: 'Web redeem code',
    webRedeemIssueProcessing: 'Issuing code…',
    webRedeemIssueResultLead:
      'Open apps.tomippe.jp in a browser and enter this code.',
    webRedeemIssueNote:
      'One code works for one browser (48 hours). Issue a new code for another browser.',
    webRedeemIssueRetry: 'Retry',
    webRedeemIssueRegenerate: 'Issue new code',
    webRedeemIssueCopy: 'Copy',
    webRedeemIssueCopied: 'Copied',
    webRedeemIssueExpires: (expiresAt) => `Expires: ${expiresAt}`,
    webRedeemIssueOpenLink: 'Issue Web redeem code',
  },
  zh: {
    paywallTitle: '需要 POUCHES Pro',
    paywallBody: (sheetCount, freeMax) =>
      `免费版最多可创建 ${freeMax} 张工作表（当前 ${sheetCount} 张）。第 4 张起需订阅 POUCHES Pro（月付）。`,
    purchase: (price) => `以 ${price}/月 订阅 POUCHES Pro`,
    purchaseFallback: '以每月 ¥400（含税）订阅 POUCHES Pro',
    restore: '恢复购买',
    manage: '管理订阅',
    close: '关闭',
    platformNote:
      '请在 App Store 或 Microsoft Store 购买 POUCHES Pro，然后使用兑换码激活 Web 版。',
    platformNoteIosNative:
      '请在此应用中使用 App Store 账户订阅 POUCHES Pro。订阅后可发放 Web 版兑换码。',
    platformNoteMacNative:
      '请在此应用中使用 App Store 账户订阅 POUCHES Pro。订阅后可发放 Web 版兑换码。',
    storeProductLoading: '正在从 App Store 获取订阅价格…',
    storePurchaseProcessingAppStore: '正在完成 App Store 购买…',
    subscriptionLegalTitle: '订阅:',
    subscriptionProductName: 'POUCHES Pro（月付·自动续订）',
    subscriptionDuration: '时长: 1 个月（自动续订）',
    subscriptionPriceLine: (price) => `价格: ${price}/月`,
    subscriptionPriceFallback: '价格: 每月 ¥400（含税）',
    subscriptionAutoRenew:
      '确认购买时将向您的 Apple ID 收费。除非在当前订阅期结束前至少 24 小时取消，否则将自动续订。可在 App Store 账户设置中管理或取消。',
    privacyPolicyLinkLabel: '隐私政策',
    termsOfUseLinkLabel: '使用条款（EULA）',
    freeHint: (remaining) =>
      remaining > 0 ? `免费版还可创建 ${remaining} 张工作表` : '已达到免费工作表上限',
    proActive: 'POUCHES Pro 已启用',
    paywallProManageBody:
      '您已订阅 POUCHES Pro。可通过「管理订阅」查看或取消。',
    paywallLegacyProBody:
      '您的既有用户 Pro 权益已生效。请在此发放 Web 版兑换码，以便在浏览器中使用 POUCHES Pro。',
    sidebarPro: 'Pro 已启用',
    sidebarFreeRemaining: (remaining) => `剩余${remaining}张`,
    sidebarFreeLimit: '已达上限',
    sidebarStatusClickSubscribe: '订阅',
    sidebarStatusClickManage: '管理或取消订阅',
    sidebarStatusClickRedeem: '输入兑换码',
    sidebarStatusClickIssueCode: '发放 Web 版兑换码',
    purchaseFailedTitle: '无法开始购买',
    storePurchaseProcessing: '正在打开 Microsoft Store…',
    storePurchaseOpenInStore: '在 Microsoft Store 中打开 POUCHES',
    restoreNone: '未找到可恢复的购买',
    restoreFailedTitle: '恢复购买失败',
    webRedeemTitle: 'Web 版兑换码',
    webRedeemBody:
      '在 App Store 或 Microsoft Store 订阅 POUCHES Pro 后，输入发放的兑换码以激活 Web 版。',
    webRedeemInputPlaceholder: '输入12位数字',
    webRedeemInputHint: '无需连字符，输入12位数字即可。',
    webRedeemActivate: '激活',
    webRedeemStoreNote: '请在 App Store 或 Microsoft Store 购买 POUCHES Pro。',
    webRedeemIssueTitle: 'Web 版兑换码',
    webRedeemIssueProcessing: '正在发放兑换码…',
    webRedeemIssueResultLead: '在浏览器中打开 apps.tomippe.jp，输入以下兑换码。',
    webRedeemIssueNote:
      '每个码仅适用于一个浏览器（48小时）。其他浏览器请重新发放。',
    webRedeemIssueRetry: '重试',
    webRedeemIssueRegenerate: '发放新兑换码',
    webRedeemIssueCopy: '复制',
    webRedeemIssueCopied: '已复制',
    webRedeemIssueExpires: (expiresAt) => `有效期: ${expiresAt}`,
    webRedeemIssueOpenLink: '发放 Web 版兑换码',
  },
};

export function getBillingCopy(locale = detectBillingLocale()): BillingCopy {
  return COPY[locale];
}
